/**
 * DTO-types voor het planning-bord (`GET /api/v1/planning/board`).
 *
 * Datums en tijden:
 *   - `periodeStart` / `periodeEind` / `dayEntries[].date` / `leaveBlocks[].*` →
 *     ISO 8601 datum/tijd (UTC, met Z-suffix). Client knipt zelf naar
 *     yyyy-MM-dd.
 *   - `plannedStart` / `plannedEnd` → "HH:mm" lokale dienst-tijd.
 */

// V9 (PO #12) — gedeeld accepteer-schema (API + form) gebruikt zod runtime.
import type { WerkbonBundelRefDTO } from './verzamelbon'
import { z } from 'zod'
import type {
  DayEntryStatus,
  DienstType,
  PloegSoort,
  WerkbonGoedkeuringStatus,
  WerkbonPriority,
  WerkbonStatus,
  WerkbonType,
} from './werkbonnen'
import type { CapacityTone } from './capacity'
// FO-340 (batch-40) — laag 2 (werkstatus) op de planbord-rij. `packages/types`
// mag `@streep/utils` niet importeren, dus de enum komt uit de zuster-module
// binnen dit package (die is er de canonieke bron van voor de DTO's).
import type { Werkstatus } from './medewerker-status'
// batch-63 werkbon-facturatie (FO-472) — reden bij "niet te factureren".
import type { WerkbonNietFacturabelReden } from './werkbon-facturabel-reden'
// batch-65 uitrusting-flow (FO-480) - type van de planbord-markering.
import type { PlanningBoardUitrustingAfspraakDTO } from './uitrusting-levering'
// batch-69 planbord-afspraak (FO-523) - het afspraak-blok op de rij.
import type { PlanningBoardAfspraakDTO } from './planning-afspraak'

export type PlanningView = 'dag' | 'week' | 'maand'

/**
 * FO-340 §10.3 — waarom is deze rij afwezig? Server-side afgeleid uit
 * `werkstatus` via `afwezigReden()` (`@streep/utils`), zodat het planbord de
 * enum niet zelf hoeft te interpreteren. `null` = inzetbaar.
 *
 * MARKEERT, blokkeert nooit: het bord dempt de naam en toont een badge, maar
 * elke actie op deze medewerker blijft toegestaan (gouden regel, spec §3.4).
 */
export type PlanningBoardAfwezig = 'ziek' | 'langdurig-ziek' | 'non-actief' | null

// ─── Verjaardag op het planbord (batch-48) ──────────────────────────────────
//
// PRIVACY (spec §5 + criterium 4): naar de planbord-client gaat UITSLUITEND de
// dag (dag+maand, als datum binnen de zichtbare periode) plus de keuze van dát
// jaar. NOOIT de geboortedatum zelf, het geboortejaar of de leeftijd - de
// bestaande masking blijft dus intact en de tooltip zegt "jarig", niet
// "wordt 43". Een DTO-test bewaakt dit.

export const PLANBORD_VERJAARDAG_KEUZES = ['VRIJ', 'WERKT'] as const
/**
 * De keuze zoals het planbord hem kent. `VRIJ` = "vrij op verjaardag";
 * `WERKT` = werkt die dag (in de registratie `BETAALD_ALS_FEESTDAG`, maar dat
 * is een verloningsbegrip dat op een planbord niets toevoegt).
 */
export type PlanbordVerjaardagKeuze = (typeof PLANBORD_VERJAARDAG_KEUZES)[number]

/**
 * Eén verjaardag van deze medewerker binnen de zichtbare planbord-periode.
 *
 * Per (rij, dag) levert dit de spec-shape `{ keuze: 'VRIJ' | 'WERKT' | null }`:
 * de client zoekt op `datum` en heeft dan de keuze. `keuze: null` = er is voor
 * dat jaar (nog) geen keuze vastgelegd - dan toont het bord wél het icoontje,
 * maar volgt er geen band en geen conflict (spec §3.4).
 */
export interface PlanningBoardVerjaardag {
  /** yyyy-mm-dd (UTC-dag) van de verjaardag; 29-02 valt in niet-schrikkeljaren op 28-02. */
  datum: string
  keuze: PlanbordVerjaardagKeuze | null
}

// ─── Planbord-tabs (Ronde 4 W1 PF5) ─────────────────────────────────────────
//
// Tab-labels. Welke medewerker op welk tab staat wordt sinds spec 24 bepaald
// door `PlanbordIndeling` per medewerker (zie hieronder), niet meer afgeleid
// uit employeeType.

export const PLANBORD_TABS = ['MONTEURS', 'KANTOOR', 'ALLES'] as const
export type PlanbordTab = (typeof PLANBORD_TABS)[number]

export const PLANBORD_TAB_LABELS: Record<PlanbordTab, string> = {
  MONTEURS: 'Monteurs',
  KANTOOR: 'Kantoor',
  ALLES: 'Alles',
}

// ─── Planbord-indeling per medewerker (spec 24 §B) ──────────────────────────
// Expliciete tabindeling, los van EmployeeType. BEIDE = op zowel Monteurs- als
// Kantoor-tab. NULL (niet ingesteld) telt als BEIDE.
export const PLANBORD_INDELING = ['MONTEURS', 'KANTOOR', 'BEIDE'] as const
export type PlanbordIndeling = (typeof PLANBORD_INDELING)[number]

export const PLANBORD_INDELING_LABELS: Record<PlanbordIndeling, string> = {
  MONTEURS: 'Monteurs',
  KANTOOR: 'Kantoor',
  BEIDE: 'Beide',
}

// ─── Planning-conflicten (Ronde 4 W1 PF3) ──────────────────────────────────
//
// Backend retourneert deze shape in een 409 `PLANNING_CONFLICT` response van
// /planning/move-day-entry, /werkbonnen/:id/reassign, en de werkbon-create
// endpoints. De ConflictModal in `planning-board-client.tsx` rendert de
// items en biedt 4 actie-knoppen (zie spec §3.4).

// batch-69 planbord-afspraak (FO-523): AFSPRAAK = de medewerker heeft op dat
// moment een afspraak met een klant (telt als bezet, nooit stil eroverheen).
// VERLOF komt alleen uit de afspraak-conflictcheck: de werkbon-paden melden
// verlof apart (409 LEAVE_CONFLICT, `findLeaveConflicts`).
export type PlanningConflictType = 'DUBBELE_INPLANNING' | 'ZIEKTE' | 'TRAINING' | 'AFSPRAAK' | 'VERLOF'

export interface PlanningConflictDTO {
  type: PlanningConflictType
  /** ID van bestaande dayEntry / sickLeave / kalenderEvent dat conflicteert. */
  conflicterendItemId: string
  medewerkerNaam: string
  klantNaam: string | null
  werkbonNummer: string | null
  /** yyyy-mm-dd (UTC-day). */
  datum: string
  /** "HH:mm" of null (hele-dag conflict). */
  plannedStart: string | null
  /** "HH:mm" of null (hele-dag conflict). */
  plannedEnd: string | null
  /** Status-string van het conflicterende item — vrije string, niet enum-strict. */
  status: string
}

export interface PlanningBoardEmployee {
  id: string
  firstName: string
  lastName: string
  employeeNumber: string | null
  functionTitle: string | null
  contractHoursPerWeek: number | null
  atvHoursPerWeek: number | null
  /**
   * Spec A §7g — ATV-regeling actief? Bepaalt of ATV-uren meetellen in
   * `totaalWerkbareUren` en wordt door het planbord gebruikt voor de
   * "Contract Xu/wk + Yu ATV"-tooltip.
   */
  atvEnabled: boolean
  /**
   * Spec A §12.5 — legacy v2-override op de werkbare uren (`werkUrenPerWeek`).
   * Wint als gevuld. `null` voor de standaard `contract + ATV`-cascade.
   */
  werkUrenPerWeek: number | null
  /**
   * Spec A §7g — server-berekende werkbare uren per week op peildatum.
   * Cascade: `werkUrenPerWeek ?? (contractHoursPerWeek + (atvEnabled ? atvHoursPerWeek : 0))`.
   * **DIT** is de noemer voor overplanning/% in het planbord, **niet**
   * `contractHoursPerWeek` (KERN-USER-FEEDBACK 2026-05-18: 38u contract + 2u
   * ATV moet als "0 / 40u" tonen).
   */
  totaalWerkbareUren: number
  /**
   * Ronde 4 W1 PF5 — type van de medewerker voor planbord-tab + visuele
   * differentiatie. NULL wanneer niet ingesteld (medewerker zichtbaar
   * op "Alles"-tab).
   */
  employeeType: 'ONDERHOUD' | 'STORING' | 'SOFTWARE' | 'PROJECT' | 'UNIVERSEEL' | 'KANTOOR' | null
  avatarUrl: string | null
  werkbonnen: PlanningBoardWerkbon[]
  leaveBlocks: PlanningBoardLeaveBlock[]
  /**
   * Ronde 4 W8 (spec 08 §5.2/§5.3) — capaciteit van deze medewerker over de
   * ISO-week (ma-zo) rond de peildatum, batch-berekend in `loadPlanningBoard()`
   * (punt 1: week-vast, ongeacht de dag/week/maand-view). Bron is de capaciteits-
   * engine (verlof/verzuim/feestdag afgetrokken), zodat planbord en dashboard
   * exact dezelfde bezettingscijfers tonen (CLAUDE.md één-waarheid).
   *
   * `undefined` wanneer de aanroeper `includeCapacity=false` meegeeft (default
   * = true). De frontend kleurt de medewerker-rij op `tone` (zie
   * `getEmployeeRowBg`). Uren afgerond op 2 decimalen aan de servicelaag.
   */
  capaciteit?: PlanningBoardCapaciteit
  /**
   * #72 (e) — actieve "tijdelijk niet inzetbaar"-override die de zichtbare periode
   * overlapt. Gevuld → de medewerker valt voor die periode uit de beschikbare
   * monteur-capaciteit (geen/lege capaciteit-rij) en het planbord toont een badge;
   * de eigen ingeplande werkbon blijft zichtbaar. `null`/weggelaten = inzetbaar.
   */
  nietInzetbaar?: { startDatum: string; eindDatum: string | null; reden: string | null } | null
  /**
   * FO-340 §4.2 — laag 2: inzetbaarheid NU. Een zieke VERDWEEN vroeger van het
   * bord (`status: 'ACTIEF'`-filter); nu blijft de rij staan en draagt hij zijn
   * werkstatus mee, zodat het bord hem kan MARKEREN i.p.v. verbergen. De planner
   * ziet dan een gedempte rij met een band, en geen raadselachtig gat.
   */
  werkstatus: Werkstatus
  /**
   * FO-340 §10.3 — afgeleide van `werkstatus` (server-side via `afwezigReden()`),
   * zodat het bord één veld hoeft te lezen i.p.v. de enum na te bouwen.
   * `null` = inzetbaar.
   */
  afwezig: PlanningBoardAfwezig
  /**
   * Batch-48 — verjaardagen van deze medewerker binnen de zichtbare periode
   * (meestal 0 of 1; een periode over een jaargrens kan er 2 bevatten).
   *
   * Server-side afgeleid uit dag+maand; de geboortedatum/het geboortejaar/de
   * leeftijd verlaten de API NIET. Bij `keuze: 'VRIJ'` staat er bovendien een
   * afwezigheidsband in `leaveBlocks` (type `VERJAARDAG_VRIJ`) en telt de dag
   * niet mee als beschikbare capaciteit.
   */
  verjaardagen: PlanningBoardVerjaardag[]
  /**
   * batch-65 uitrusting-flow (FO-480 §A.4) — lopende lever-/afhaalafspraken op
   * de dagregels van deze medewerker in de zichtbare periode (als ontvanger of
   * als meenemer): een klein icoon "uitrusting meenemen". Batch-geladen met één
   * query voor het hele bord; weggelaten = geen afspraken.
   */
  uitrustingAfspraken?: PlanningBoardUitrustingAfspraakDTO[]
  /**
   * batch-66 planbord-klantfilter (FO-487) - alleen gezet bij een actief
   * klantfilter: heeft deze medewerker in de zichtbare periode minstens één
   * dagregel bij de gefilterde klant(en)? Server-side afgeleid uit dezelfde
   * dagregel-bron als de blokken. Het bord zet de ingeplande rijen bovenaan en
   * de rest onder de scheidingsregel; een rij met `false` staat er omdat de
   * schakelaar "Toon ook niet-ingeplande monteurs" aan staat of omdat de
   * medewerker expliciet gekozen is in het medewerkerfilter.
   */
  ingeplandBijKlant?: boolean
  /**
   * batch-69 planbord-afspraak (FO-523) - afspraken met een klant waaraan deze
   * medewerker deelneemt in de zichtbare periode (geen werkbon, geen uren; wel
   * bezet). Batch-geladen met één query voor het hele bord; afgeschermde inhoud
   * is server-side weggelaten. Weggelaten = geen afspraken (en een oudere
   * API-revisie tijdens een rolling deploy breekt het bord niet).
   */
  afspraken?: PlanningBoardAfspraakDTO[]
}

/**
 * Capaciteits-blok per medewerker-rij op het planbord. `tone` is de gedeelde
 * 5-waarden-`CapacityTone` uit de engine (één bron), niet een eigen mini-union.
 */
export interface PlanningBoardCapaciteit {
  beschikbareUren: number
  geplandeUren: number
  restCapaciteit: number
  bezettingsgraad: number
  tone: CapacityTone
  verlofUren: number
  verzuimUren: number
  /**
   * Batch-48 — uren die wegvallen doordat de medewerker deze week jarig is en
   * "vrij op verjaardag" koos. Los van `verlofUren` (het kost géén verlofsaldo)
   * en los van de feestdaguren, zodat de tooltip niet liegt over de reden.
   */
  verjaardagUren: number
  /**
   * Punt 5 — geplande REIStijd (uren, ISO-week) van deze medewerker, LOS van de
   * werktijd in `geplandeUren`. Bewust apart zodat het planbord werktijd en
   * reistijd niet op één hoop gooit.
   */
  reisUren: number
}

// ─── batch-63 werkbon-facturatie (FO-472) — facturatiestatus op het planbord ──
//
// De status komt UITSLUITEND uit dezelfde resolver als de werkbonnenlijst
// (`facturatieStatusVan`, @streep/utils) en wordt server-side vertaald naar de
// vijf planbord-standen (`planbordFacturatieVan`). Geen tweede waarheid.

export const PLANBORD_FACTURATIE_STATUSSEN = [
  'GEFACTUREERD',
  'VERZONDEN_FINQLE',
  'DEELS_GEFACTUREERD',
  'TE_FACTUREREN',
  'NIET_FACTURABEL',
] as const
/**
 *   GEFACTUREERD        eigen factuur, Finqle-factuur (INVOICED) of handmatig;
 *   VERZONDEN_FINQLE    naar Finqle verzonden, wacht op de factuur;
 *   DEELS_GEFACTUREERD  deze werkbon nog niet, maar een deel van zijn reeks
 *                       (hoofdbon + weekbonnen) wel;
 *   TE_FACTUREREN       nog te factureren;
 *   NIET_FACTURABEL     interne (kantoor)bon of bewust niet te factureren.
 */
export type PlanbordFacturatieStatus = (typeof PLANBORD_FACTURATIE_STATUSSEN)[number]

/**
 * batch-63 (FO-472) — het filter "facturatiestatus" op het planbord. Werkt op de
 * geladen periode (het bord laadt de volledige periode; geen afkap).
 *   TE_FACTUREREN  nog te factureren (ook "deels gefactureerd": deze bon nog niet);
 *   GEFACTUREERD   gefactureerd of naar Finqle verzonden.
 */
export const PLANBORD_FACTURATIE_FILTERS = ['ALLE', 'TE_FACTUREREN', 'GEFACTUREERD'] as const
export type PlanbordFacturatieFilter = (typeof PLANBORD_FACTURATIE_FILTERS)[number]

export const PLANBORD_FACTURATIE_FILTER_LABELS: Record<PlanbordFacturatieFilter, string> = {
  ALLE: 'Alle facturatiestatussen',
  TE_FACTUREREN: 'Alleen nog te factureren',
  GEFACTUREERD: 'Alleen gefactureerd',
}

export function isPlanbordFacturatieFilter(v: string | null | undefined): v is PlanbordFacturatieFilter {
  return (PLANBORD_FACTURATIE_FILTERS as readonly string[]).includes(v ?? '')
}

/**
 * batch-63 (FO-472) — waarom een werkbon op het planbord niet verplaatst,
 * hertoegewezen of uit de planning gehaald kan worden. Zelfde regel als de
 * API-routes (422 `WERKBON_LOCKED`), zodat het bord het vooraf uitlegt.
 */
export const PLANBORD_VERGRENDEL_REDENEN = ['GEFACTUREERD', 'VERLOOND', 'IN_GOEDKEURING'] as const
export type PlanbordVergrendelReden = (typeof PLANBORD_VERGRENDEL_REDENEN)[number]

export interface PlanbordVergrendelingDTO {
  reden: PlanbordVergrendelReden
  /** Uitleg-zin, bv. "De werkbon zit in de goedkeuring (goedgekeurd door de klant)." */
  uitleg: string
}

export interface PlanningBoardFacturatieDTO {
  status: PlanbordFacturatieStatus
  /** Hetzelfde label als de werkbonnenlijst ("Gefactureerd via factuur F2026-0012"). */
  label: string
  /** Kant-en-klare tooltip (datum dd-MM-yyyy, factuurnummer alleen met FACTUREN.canView). */
  tooltip: string
  /** ISO-tijdstip van de facturatie/verzending; null als niet bekend. */
  op: string | null
  /** Factuurnummer (eigen factuur of Finqle-factuur). null zonder FACTUREN.canView. */
  factuurNummer: string | null
  /** Eigen factuur voor de doorklik. null zonder FACTUREN.canView. */
  factuurId: string | null
  /** Finqle-facturatieverzoek (FO-406) voor de doorklik. null zonder FACTUREN.canView. */
  verzoekId: string | null
  /** Bij NIET_FACTURABEL: de reden; null = zonder reden (controleren). */
  nietFacturabelReden: WerkbonNietFacturabelReden | null
  /** Alleen bij een reeks met meer dan één werkbon: hoeveel daarvan gefactureerd zijn. */
  reeks: { gefactureerd: number; totaal: number } | null
  /**
   * Verplaatsen, hertoewijzen en uit de planning halen weigert de API
   * (WERKBON_LOCKED): de werkbon hangt aan een factuur, is verloond of zit in de
   * goedkeuring. Het bord legt dat vooraf uit. `null` = vrij te verplaatsen.
   */
  vergrendeling: PlanbordVergrendelingDTO | null
  /**
   * batch-65 (FO-484 §C.7) — "in bundel VB-…": de verzamelbon waar de bon in
   * zit (doorklik), of null. Optioneel zodat bestaande bouwers niet breken.
   */
  bundel?: WerkbonBundelRefDTO | null
}

/**
 * Weektotaal in de kop van het planbord: de werkbonnen die op de tab "Te
 * factureren" staan (zelfde fragment als de lijst) met een dagregel in deze
 * week. Server-side geteld over de VOLLEDIGE set, niet over de geladen blokken.
 */
export interface PlanningBoardFacturatieWeekDTO {
  /** ISO-week 'ww-jjjj' (zelfde notatie als `Werkbon.weekNr`). */
  weekNr: string
  /** Maandag en zondag van de week (yyyy-mm-dd). */
  van: string
  tot: string
  aantalWerkbonnen: number
  /** Som van de uren van die werkbonnen binnen deze week. */
  uren: number
}

export interface PlanningBoardWerkbon {
  id: string
  werkbonNumber: string
  status: WerkbonStatus
  /**
   * Twee-staps goedkeuringsflow (intern → klant) die start zodra de werkbon
   * AFGEROND is. Het planbord toont dit als rand + badge op het blok zodat
   * "afgerond / wacht op goedkeuring / goedgekeurd / afgekeurd" zichtbaar is.
   * `null` = (nog) geen flow.
   */
  goedkeuringStatus: WerkbonGoedkeuringStatus | null
  type: WerkbonType
  startDatum: string
  eindDatum: string
  weekNr: string | null
  uurtarief: number
  /** DAGDIENST / PLOEGEN_2 / PLOEGEN_3 / PLOEGEN_5 / CONSIGNATIE — voor sidebar-label. */
  dienstType: DienstType
  /** Welke ploeg deze dag draait — bij DAGDIENST = DAG. */
  ploegSoort: PloegSoort | null
  /** Werkbon-opmerkingen (free text) — getoond als sub-tekst op de pill. */
  opmerkingen: string | null
  /** Werkbon-omschrijving — fallback voor pill-label wanneer geen order/client. */
  omschrijving: string | null
  /** Verplichte werkbon-klant (Prisma); nullable in DTO voor backwards-compat. */
  client: { id: string; name: string } | null
  /** Onderdeel van een recurring-serie — getoond als badge in sidebar. */
  isRecurring: boolean
  // PR-4 (spec 02 §6.1) — consignatie-vlag voor amber styling + bell-icoon.
  isConsignatie: boolean
  consignatieType: 'WEEK_NACHT' | 'WEEKEND' | 'FEESTDAG' | 'CUSTOM' | null
  dayEntries: PlanningBoardDayEntry[]
  // Ronde 4 W6 — `order` is vervangen door directe werkbon-velden:
  // referentie (was order.title) + clientLocation (was order.clientLocation).
  referentie: string | null
  clientLocation: { id: string; name: string } | null
  // Fase 3 (cursus-multidag) — bron-koppeling op het planbord.
  bronType?: 'CURSUS' | 'KALENDER' | null
  bronLabel?: string | null
  /** Verlonende BV (Spec 3 / batch-juni B) - bron van de celkleur op het planbord. */
  entity: { id: string; korteNaam: string | null; kleur: string | null } | null
  /** FO-008 — besloten werkbon (slotje-markering voor wie het mag zien). */
  besloten: boolean
  /**
   * FO-008 — afgeschermd voor de huidige planbord-viewer? `true` = de server gaf
   * alleen een neutraal "Besloten"-blok terug: `omschrijving`/`referentie`/
   * `opmerkingen`/`client`/`clientLocation`/`entity` zijn null en `werkbonNumber`
   * is geneutraliseerd. De dag-blokken (tijd/uren) blijven zodat capaciteit telt.
   * Niet te openen (detail-endpoint geeft 403). Server-side bepaald (CC.SCOPE).
   */
  afgeschermd: boolean
  /** batch-63 (FO-472) — facturatiestatus van deze werkbon (zelfde resolver als de lijst). */
  facturatie: PlanningBoardFacturatieDTO
}

export interface PlanningBoardDayEntry {
  id: string
  date: string
  plannedStart: string
  plannedEnd: string
  /** Werkelijke begintijd "HH:mm" (WerkbonDayEntry.actualStart), of null. Planbord sorteert hierop met fallback op plannedStart. */
  actualStart: string | null
  plannedHours: number
  hours: number
  pauzeMinutes: number
  overuren: number
  reisKm: number
  reisMinuten: number
  reisVergoeding: number
  /**
   * Per-dag reisuren-skip: wanneer `true` rekent de engine geen reistijd voor
   * deze dag (de monteur/planner zette "geen reisuren"). Het planbord toont dan
   * een 'geen reisuren'-label en biedt de toggle aan i.p.v. km/min/vergoeding.
   */
  geenReisuren: boolean
  status: DayEntryStatus
  /**
   * Server-side afgeleid via `requiresUrenAfwijkingReden` (@streep/utils, één
   * bron): true wanneer de werkelijke uren significant afwijken van de
   * planning of de dag als afwezig is gemarkeerd. Het planbord toont dan een
   * onopvallende afwijking-marker op het blok.
   */
  afwijkend: boolean
  /** Toelichting bij de afwijking (`WerkbonDayEntry.afwijkingReden`), of null. */
  afwijkingReden: string | null
}

/**
 * Batch-48 — pseudo-`type` van de verjaardag-band. De band hergebruikt bewust
 * het verlofband-mechanisme (`leaveBlocks`) i.p.v. een tweede mechanisme: één
 * plek die "deze rij is deze dag afwezig" tekent. Het is géén `LeaveType` uit
 * Prisma - `PlanningBoardLeaveBlock.type` is een vrije string en de client
 * mapt hem via zijn eigen labeltabel.
 */
export const PLANBORD_VERJAARDAG_BAND_TYPE = 'VERJAARDAG_VRIJ'

export interface PlanningBoardLeaveBlock {
  id: string
  type: string
  // FO-021 — alleen de weergave-statussen bereiken het bord: actief (groen) +
  // aangevraagd (oranje). Afgekeurd (AFGEWEZEN/AFGEKEURD_TEAMLEIDER/AFGEKEURD),
  // OVERRULED, INGETROKKEN en CONCEPT worden server-side weggefilterd
  // (VERLOF_WEERGAVE_STATUSSEN) en zitten dus bewust niet in deze union.
  status:
    | 'AANGEVRAAGD'
    | 'GOEDGEKEURD'
    | 'INGEDIEND'
    | 'GOEDGEKEURD_TEAMLEIDER'
    | 'GECLASSIFICEERD'
    | 'DEFINITIEF'
    | 'AANVULLENDE_VRAAG'
  startDate: string
  endDate: string
  // Planbord-herontwerp — verrijking voor het `LeaveBlock`-component (reden +
  // dagdeel + hover-popup). De API leidt deze af uit de `LeaveRequest`:
  //   - `reden`     ← `LeaveRequest.reason` (ontbreekt → weggelaten)
  //   - `isHeledag` ← `!LeaveRequest.isPartialDay`
  // `startTijd`/`eindTijd`/`gemeldDoor` hebben (nog) geen bronveld op
  // `LeaveRequest`; ze blijven optioneel zodat het UI-component ze toont zodra
  // er wel een bron is, zonder nu lege/verzonnen tijden te tonen.
  /** Reden/toelichting van de aanvraag (`LeaveRequest.reason`). */
  reden?: string
  /** "HH:mm" begintijd bij een deel-dag; weggelaten bij een hele dag. */
  startTijd?: string
  /** "HH:mm" eindtijd bij een deel-dag; weggelaten bij een hele dag. */
  eindTijd?: string
  /** `true` = hele dag (geen deel-dag); afgeleid uit `!isPartialDay`. */
  isHeledag: boolean
  /**
   * Aantal verlof-uren op deze dag (`LeaveRequest.hours`). Bij een deel-dag toont
   * het planbord dit (bv. "45min", "4u") i.p.v. een misleidend "Halve dag" — een
   * deel-dag is lang niet altijd een halve dag. Weggelaten als de bron geen uren heeft.
   */
  uren?: number
  /** Bij ziekte: wie de melding deed (optioneel — geen bron zolang ZIEK een `LeaveRequest` is). */
  gemeldDoor?: string
  /**
   * Batch-6 — structureel weekpatroon (ouderschapsverlof): de yyyy-mm-dd's van
   * de patroon-dagen. Gevuld → het blok geldt alléén op deze dagen binnen
   * [startDate, endDate] ("elke woensdagmiddag" blokkeert geen donderdag).
   */
  patroonDagen?: string[]
  /**
   * batch-57 (FO-418) - kalenderdagen (yyyy-mm-dd) binnen deze aanvraag die
   * volgens het rooster 0 uur kosten (weekend of 0-urendag). Die dagen krijgen
   * op het planbord een eigen band "Geblokkeerd (roostervrij)" i.p.v. de gewone
   * verlofband en kosten geen saldo. Leeg/afwezig = gewone verlofdag(en); bij
   * een handmatige uren-override (`LeaveRequest.urenHandmatig`) blijft hij
   * bewust leeg, want dan kost de dag wel degelijk uren.
   */
  blokkadeDagen?: string[]
}

export interface PlanningBoardUnplanned {
  id: string
  werkbonNumber: string
  type: WerkbonType
  weekNr: string | null
  startDatum: string
  eindDatum: string
  totalDagen: number
  dayEntries: PlanningBoardDayEntry[]
  omschrijving: string | null
  client: { id: string; name: string } | null
  employee: { id: string; firstName: string; lastName: string }
  // Ronde 4 W6 — `order` veld verwijderd; klant + referentie zijn al direct
  // op het rij-niveau aanwezig.
  referentie: string | null
  clientLocation: { id: string; name: string } | null
  /** FO-008 — besloten werkbon. */
  besloten: boolean
  /** FO-008 — afgeschermd voor de huidige viewer (neutraal "Besloten"-blok, niet te openen). */
  afgeschermd: boolean
}

/**
 * FO-340 §4.2/§10.3 — telling van de medewerkers die het raster WEGLIET omdat de
 * bijbehorende toggle uit stond.
 *
 * Zonder deze telling kan het bord alleen "Toon langdurig zieken" zeggen en niet
 * "3 langdurig ziek": ze zitten immers niet in `employees`. En zonder telling is
 * een ingeklapte groep niet te onderscheiden van een lege — dan is de rij
 * opnieuw onvindbaar en is dit FO terug bij af (§2.3 #2).
 *
 * Staat een toggle AAN, dan is zijn teller `0`: de rijen staan dan gewoon in
 * `employees` en zijn daar te tellen op `afwezig`.
 */
export interface PlanningBoardAfwezigenIngeklapt {
  langdurigZiek: number
  nonActief: number
}

// ─── batch-66 planbord-klantfilter (FO-487) - de klantplanning-weergave ──────
//
// Met een klantfilter toont het bord standaard alleen de monteurs die in de
// zichtbare periode een dagregel bij die klant(en) hebben. Alles wat de planner
// daarbij moet weten (hoeveel rijen er verborgen zijn, hoeveel monteurs en
// diensten er per dag staan) komt hier server-side uit; het bord telt niets
// over de geladen rijen.

/** Inzet bij de gefilterde klant(en) over een dag, een week of de hele periode. */
export interface PlanningBoardKlantInzetTelling {
  /** Aantal VERSCHILLENDE monteurs (een monteur met twee diensten telt één keer). */
  monteurs: number
  /** Aantal dagregels (diensten), bemand en onbemand samen. */
  diensten: number
  /** Hoeveel van die diensten nog geen monteur hebben ("wordt ingepland"). */
  onbemand: number
}

export interface PlanningBoardKlantInzetDag extends PlanningBoardKlantInzetTelling {
  /** Kalenderdag `yyyy-MM-dd`. */
  datum: string
}

export interface PlanningBoardKlantInzetWeek extends PlanningBoardKlantInzetTelling {
  /** ISO-week 'ww-jjjj' (zelfde notatie als `Werkbon.weekNr`). */
  weekNr: string
  /** Eerste en laatste dag van de week BINNEN de zichtbare periode (yyyy-MM-dd). */
  van: string
  tot: string
}

/**
 * Een medewerker binnen de scope van het bord, ook als hij geen rij heeft
 * (medewerkerfilter of klant-doorsnede). Voedt de medewerker-dropdown en de
 * monteur-kiezer van de bijplan-regel.
 */
export interface PlanningBoardAvailableEmployee {
  id: string
  firstName: string
  lastName: string
  /**
   * FO-340 - inzetbaarheid nu; voedt de vraag "Toch inplannen?" vóór een
   * toewijzing. Verplicht: zonder dit veld zou het bord de vraag stil overslaan
   * voor precies de monteurs zonder rij.
   */
  werkstatus: Werkstatus
  /** Batch-48 - verjaardagen in de zichtbare periode (alleen dag + keuze). */
  verjaardagen: PlanningBoardVerjaardag[]
}

export interface PlanningBoardKlantweergaveDTO {
  /** De klant(en) uit het filter, op naam. Voedt de kop en de doorkliks. */
  klanten: Array<{ id: string; naam: string }>
  /**
   * Locaties waar de klant(en) in deze periode dagregels hebben, over de hele
   * klantinzet (dezelfde set als de telling hieronder). Een besloten werkbon
   * draagt hier nooit aan bij (FO-008), ook niet voor wie hem mag inzien: de
   * kop geeft niets prijs wat een blok kan verbergen.
   */
  locaties: Array<{ id: string; naam: string }>
  /** Stond de schakelaar "Toon ook niet-ingeplande monteurs" aan voor deze DTO? */
  toonNietIngepland: boolean
  /**
   * Aantal rijen binnen de scope van het bord (BV, tab, zoekterm, FO-340-venster)
   * dat de klantweergave WEGLAAT zolang de schakelaar uit staat. Server-side
   * geteld op dezelfde scope zonder de klant-doorsnede. Staat de schakelaar aan,
   * dan zijn dit precies de rijen die erbij gehaald zijn; het getal blijft dus
   * gelijk in beide standen en het label hoeft niet te verspringen.
   *
   * Bij een medewerkerfilter is dit 0: een expliciet gekozen medewerker blijft
   * altijd staan, er valt dan niets te verbergen.
   */
  verborgenRijen: number
  /**
   * Monteurs die in de periode WEL bij de klant staan maar niet als rij op het
   * bord: weggefilterd door tab, BV, zoekterm of medewerkerfilter, ingeklapt als
   * langdurig ziek of non-actief, of buiten hun dienstverband. De telling
   * hieronder gaat over de hele klantinzet en telt hen dus mee; dit getal
   * verklaart het verschil met het raster, zodat de kop nooit stil afwijkt.
   */
  monteursBuitenBeeld: number
  /**
   * De telling gaat over ALLE dagregels bij de klant(en) in de periode (zelfde
   * bron als de blokken, zonder de medewerker-scope van het bord): dit is de
   * planning van de klant, niet "wat er na je filters overblijft".
   *
   * Eén regel per kalenderdag van de zichtbare periode (ook dagen zonder inzet).
   */
  perDag: PlanningBoardKlantInzetDag[]
  /** Eén regel per ISO-week in de zichtbare periode; `monteurs` is distinct per week. */
  weken: PlanningBoardKlantInzetWeek[]
  /** Totaal over de zichtbare periode; `monteurs` is distinct over de periode. */
  totaal: PlanningBoardKlantInzetTelling
}

// ─── batch-49 in-te-plannen (FO-398) — het vak bovenaan het planbord ─────────
//
// GEEN nieuw model: dit is de bestaande query op `WerkbonDayEntry.employeeId =
// null` binnen het bord-venster, verrijkt met herkomst + urgentie zodat de
// planner in één blik ziet wát er open ligt en hoe hard het is.

/**
 * Waarom staat deze dienst in het vak? Spiegelt de Prisma-enum
 * `DayEntryOntkoppeldHerkomst`, plus `NIEUW` voor een werkbon die zonder monteur
 * is aangemaakt (daar is niets ontkoppeld, dus staat er niets in de DB).
 */
export type InTePlannenHerkomst =
  | 'UITDIENST'
  | 'UITDIENST_OPSCHONING'
  | 'LOSGEKOPPELD'
  | 'ZIEKTE'
  | 'VERLOF'
  | 'NIEUW'

export const IN_TE_PLANNEN_HERKOMST_LABELS: Record<InTePlannenHerkomst, string> = {
  UITDIENST: 'Uitdienst',
  UITDIENST_OPSCHONING: 'Uitdienst (opschoning)',
  LOSGEKOPPELD: 'Losgekoppeld',
  ZIEKTE: 'Ziekte',
  VERLOF: 'Verlof',
  NIEUW: 'Nieuw',
}

/** Eén onbemande dag binnen een item (de werkbon-groepering). */
export interface InTePlannenDagDTO {
  dayEntryId: string
  /** ISO 8601 (UTC-middernacht), zoals elke dagregel-datum. */
  date: string
  plannedStart: string
  plannedEnd: string
  plannedHours: number
  status: DayEntryStatus
  /**
   * §7.7 — een onbemande regel in een vergrendelde week kan alleen via
   * randpaden ontstaan. Hij blijft zichtbaar (slot-icoon) maar is niet te
   * muteren buiten het correctie-regime.
   */
  weekVergrendeld: boolean
}

/**
 * Eén item = één werkbon met zijn onbemande dagen (§3.1: "reeksen van dezelfde
 * werkbon gegroepeerd"). Een deels bemande reeks levert alleen de onbemande
 * dagen; `totaalDagen` houdt "2 van 5 dagen" leesbaar (§7.2).
 */
export interface InTePlannenItemDTO {
  werkbonId: string
  werkbonNumber: string
  type: WerkbonType
  client: { id: string; name: string } | null
  clientLocation: { id: string; name: string } | null
  referentie: string | null
  omschrijving: string | null
  dienstType: DienstType | null
  ploegSoort: PloegSoort | null
  /** §7.3 — consignatie/standby gaat mee, met eigen badge. */
  isConsignatie: boolean
  prioriteit: WerkbonPriority
  /** Deadline van de werkbon (ISO) — voedt sortering + de deadline-regel. */
  deadline: string | null
  herkomst: InTePlannenHerkomst
  /** "Uitdienst Daan Jansen" / "Losgekoppeld door Tim" — al samengesteld. */
  herkomstLabel: string
  /** Wanneer de regel in het vak belandde (ISO); `null` bij herkomst NIEUW. */
  ontkoppeldOp: string | null
  /** De onbemande dagen, oplopend op datum. */
  dagen: InTePlannenDagDTO[]
  /** Totaal aantal dagen van deze werkbon in het venster (bemand + onbemand). */
  totaalDagen: number
  /** Eerste onbemande dag (ISO) — de urgentie-anker. */
  eersteDatum: string
  /** Uren tot de eerste dienst; negatief = de dienst ligt in het verleden. */
  urenTotEersteDienst: number
  /** < 48 uur tot uitvoering → rode markering (§3.1). */
  urgent: boolean
  /** Dienst ligt in het verleden zonder uren (§7.1: "verlopen"-markering). */
  verlopen: boolean
  /** FO-008 — besloten werkbon: neutraal blok, niet te openen. */
  besloten: boolean
  afgeschermd: boolean
  /** batch-63 (FO-472) — dezelfde facturatie-markering als de blokken op het bord. */
  facturatie: PlanningBoardFacturatieDTO
}

/**
 * Het vak als geheel. `totaal` telt server-side over de VOLLEDIGE gefilterde set
 * (nooit `items.length` na een cap) zodat de teller in de kop klopt; `afgekapt`
 * zegt eerlijk of er meer items zijn dan getoond (CLAUDE.md — geen stille afkap).
 */
export interface InTePlannenVakDTO {
  items: InTePlannenItemDTO[]
  /** Aantal onbemande dagregels in het venster (niet: aantal werkbonnen). */
  totaalDagen: number
  /** Aantal werkbon-items in het venster, vóór de weergave-cap. */
  totaal: number
  afgekapt: boolean
  /** Hoeveel items zijn urgent (< 48 uur) — voedt de rode teller in de kop. */
  urgent: number
}

export interface PlanningBoardDTO {
  periodeStart: string
  periodeEind: string
  employees: PlanningBoardEmployee[]
  unplanned: PlanningBoardUnplanned[]
  /**
   * batch-49 in-te-plannen (FO-398 §3.1) — het vak bovenaan het bord. Zelfde
   * onbemande dagregels als `unplanned`, maar verrijkt (herkomst/urgentie/
   * prioriteit) en met een eerlijke teller.
   *
   * DIT is sinds batch-49 de bron voor het planbord én de Excel-export;
   * `unplanned` hierboven is legacy en wordt nergens in de apps meer gerenderd.
   */
  inTePlannen: InTePlannenVakDTO
  /** Bron voor de medewerker-filterdropdown: alle medewerkers die in scope
   *  vallen (planbord-raster-selectie + BV-filter + topbar-search) ONGEACHT het
   *  actieve `employeeIds`-filter. Voorkomt dat de dropdown zichzelf inkrimpt
   *  zodra iemand is aangevinkt.
   *
   *  batch-66 planbord-klantfilter (FO-487) - bij een klantfilter is dit ook de
   *  scope ZONDER de klant-doorsnede: de monteurs die het bord weglaat staan
   *  hier wel in, zodat de bijplan-regel er iemand uit kan kiezen. Daarom
   *  dragen de entries nu `werkstatus` en `verjaardagen` mee: het bord moet
   *  vóór het inplannen dezelfde afwezigheids- en verjaardagsvraag kunnen
   *  stellen als bij een monteur die wel een rij heeft. Verplicht in het type,
   *  zodat de compiler elke bron in de API afdwingt; het bord leest ze toch
   *  defensief (`?? []`, en een onbekende werkstatus geeft geen vraag) voor het
   *  korte venster waarin een nieuwe backoffice tijdens een rolling deploy nog
   *  tegen de vorige API-revisie praat. */
  availableEmployees: PlanningBoardAvailableEmployee[]
  /**
   * FO-340 — hoeveel rijen zitten er achter de (standaard ingeklapte) toggles
   * `toonLangdurigZiek` / `toonNonActief`? Voedt het label van de samengevouwen
   * groep onderaan het bord.
   */
  afwezigenIngeklapt: PlanningBoardAfwezigenIngeklapt
  /**
   * batch-63 (FO-472) — per zichtbare ISO-week het "te factureren"-totaal
   * (server-side over de volledige set; bij precies één klantfilter alleen die
   * klant). Doorklik: de werkbonnenlijst op tab Te factureren, zelfde week.
   */
  facturatieWeken: PlanningBoardFacturatieWeekDTO[]
  /**
   * batch-63 (FO-472) — mag de viewer factuurnummers en de doorklik naar de
   * factuur zien (FACTUREN.canView)? Zonder dit recht toont het bord wel het
   * icoon, maar geen nummer en geen link (server-side al gestript).
   */
  magFacturenZien: boolean
  /**
   * batch-66 planbord-klantfilter (FO-487) - de klantplanning-weergave. `null`
   * zonder klantfilter. Optioneel in het type zodat een oudere API-revisie
   * tijdens een rolling deploy (en bestaande test-literals) niet breken; het
   * bord leest een ontbrekend veld als `null`.
   */
  klantweergave?: PlanningBoardKlantweergaveDTO | null
}

// ─── Planning sidebar — klanten-tab (Ronde-2 BLOK 1) ──────────────────────────
//
// `GET /api/v1/planning/sidebar-clients?weekStart=&weekEnd=` levert per actieve
// klant een sleepbare card-DTO. Aggregatie over de zichtbare week, klanten
// zonder werkbonnen worden óók getoond (counts = 0) zodat de planner een
// nieuwe werkbon kan beginnen.
export interface SidebarClient {
  id: string
  clientNumber: string
  name: string
  city: string | null
  /** Aantal actieve werkbonnen voor de geselecteerde week (overlap met week-bereik). */
  werkbonCount: number
  /** Aantal unieke ingeplande monteurs deze week. */
  monteurCount: number
  /** Heeft actieve service-overeenkomst (placeholder — gebaseerd op default-tarief). */
  hasServiceContract: boolean
  /** Meest voorkomende werkbon-type voor deze klant — voor type-pill. */
  primaryType: WerkbonType | null
}

export interface PlanningSidebarClientsResponse {
  data: SidebarClient[]
}

// ─── Dekkingsoverzicht (#36) ────────────────────────────────────────────────
//
// `GET /api/v1/planning/dekking?van=&tot=` — per klant-rooster (ClientWorkSchedule
// met rotatiepatroon) en per ISO-week het verwachte aantal diensten (niet-`X`-codes
// in de week) vs. het werkelijk geplande aantal, met een classificatie.
// V9 (PO #12) — een geaccepteerde afwijking telt niet langer als probleem:
// classificatie GEACCEPTEERD onderdrukt TE_WEINIG/TE_VEEL in het overzicht.
// FO-348 (batch-40) — `gepland` telt sinds batch-40 de GEVULDE DIENST-SLOTS van
// dít rooster (niet meer de kalenderdagen met een bon van de klant): realisatie
// wordt per (rooster, week) gematcht, consignatie/interne bonnen tellen niet, en
// bonnen zonder rooster-koppeling bij een multi-rooster-klant verschijnen als een
// aparte NIET_TOEGEKEND-regel i.p.v. bij elk rooster mee te tellen.
export type DekkingClassificatie =
  | 'COMPLEET'
  | 'TE_WEINIG'
  | 'TE_VEEL'
  | 'GEACCEPTEERD'
  /**
   * FO-348 §3.1 — restcategorie: entries op bonnen zonder `workScheduleId` bij een
   * klant met meerdere roosters. Ze zijn ergens gedraaid, maar niet toewijsbaar;
   * eerder telden ze bij álle roosters mee (structureel TE_VEEL/vervuiling).
   * Geen verwachting, dus geen afwijking - wel een fix-actie (rooster koppelen).
   */
  | 'NIET_TOEGEKEND'

/** V9 (PO #12) — soort acceptatie van een dekkingsafwijking. */
export type DekkingAcceptatieType = 'EENMALIG' | 'STRUCTUREEL'

export interface DekkingAcceptatieDTO {
  id: string
  type: DekkingAcceptatieType
  reden: string
  /** Bij EENMALIG: de specifieke week (yyyy-mm-dd maandag). Null bij STRUCTUREEL. */
  weekStart: string | null
  geaccepteerdDoorNaam: string | null
  createdAt: string
}

export interface DekkingWeekRijDTO {
  clientId: string
  clientNaam: string
  roosterNaam: string
  /**
   * FO-348 — het rooster waar deze regel over gaat. `null` op de
   * NIET_TOEGEKEND-restregel (die hoort per definitie bij geen enkel rooster).
   * Maakt de regel uniek: een klant kan meerdere roosters per week hebben.
   */
  workScheduleId: string | null
  /** FO-348 — true zodra de klant meer dan één rooster heeft (UI toont dan de roosternaam). */
  meerdereRoosters: boolean
  /** "W18 2026" (leesbaar). */
  weekLabel: string
  /** ISO-datum van de maandag van de week. */
  weekStart: string
  verwacht: number
  /**
   * FO-348 §3.3 — de dienst-slots met minstens één monteur, dus altijd
   * `<= verwacht`. Werk op een dag die het rooster niet draait vult geen slot en
   * kan een gat dus niet meer maskeren; dat werk staat in `extraDagen`.
   */
  gepland: number
  /**
   * FO-348 — kalenderdagen met werk buiten elk roosterslot. Dít is "te veel":
   * de klant draaide diensten die het rooster niet vroeg. Op de
   * NIET_TOEGEKEND-regel: de dagen die aan geen rooster te koppelen zijn.
   */
  extraDagen: number
  /**
   * FO-348 §4 — de slot-opbouw achter verwacht/gepland, bv.
   * `"ma D, di D, wo D (leeg), do D, vr D"`. Zo ziet de planner wélk slot open
   * staat i.p.v. alleen "4 van 5". Leeg op de NIET_TOEGEKEND-regel.
   */
  slotDetail: string
  /** FO-348 §3.5 — de op dit rooster ingestelde speling (0 = elke afwijking telt). */
  tolerantie: number
  classificatie: DekkingClassificatie
  /**
   * batch-49 in-te-plannen (FO-398 §3.3) — dagregels van deze klant in deze week
   * ZONDER uitvoerder. Ze tellen bewust NIET mee in `gepland` (een onbemande
   * dienst vult geen slot), maar mogen ook niet stil verdwijnen: dan lijkt het
   * gat een planningsfout terwijl er gewoon iemand toegewezen moet worden. De
   * dekkingsmail benoemt ze apart ("3 diensten zonder uitvoerder", §8).
   */
  onbemand: number
  // V9 (PO #12) — als de afwijking (eenmalig of structureel) is geaccepteerd,
  // de details zodat het overzicht het toont + intrekken mogelijk maakt.
  acceptatie: DekkingAcceptatieDTO | null
}

export interface DekkingResponse {
  van: string
  tot: string
  rijen: DekkingWeekRijDTO[]
}

/** V9 (PO #12) — request om een dekkingsafwijking te accepteren. Gedeeld
 *  schema (API + form): reden verplicht. */
export const accepteerDekkingAfwijkingSchema = z.object({
  clientId: z.string().min(1),
  /** Maandag van de week (yyyy-mm-dd). Verplicht ook bij STRUCTUREEL (anker-week). */
  weekStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  type: z.enum(['EENMALIG', 'STRUCTUREEL']),
  reden: z.string().trim().min(3, 'Geef een reden op (min. 3 tekens).').max(500),
  /** Snapshot van de afwijking op het moment van accepteren. */
  verwacht: z.number().int().min(0),
  gepland: z.number().int().min(0),
  /**
   * FO-348 — dagen met werk buiten het rooster. Sinds batch-40 kan `gepland`
   * niet meer boven `verwacht` uitkomen (het zijn gedekte slots), dus een
   * TE_VEEL is uitsluitend hieraan te herkennen. Optioneel: oudere clients die
   * het veld niet sturen accepteren nog steeds een tekort.
   */
  extraDagen: z.number().int().min(0).optional(),
})
export type AccepteerDekkingAfwijkingInput = z.infer<typeof accepteerDekkingAfwijkingSchema>

export type DekkingAcceptatieMutateResponse = { data: DekkingAcceptatieDTO }

// ─── FO-348 — fix-actie "rooster koppelen" op de NIET_TOEGEKEND-regel ────────

/** Eén werkbon zonder rooster-koppeling in de restcategorie van een week. */
export interface OngekoppeldeWerkbonDTO {
  id: string
  werkbonNumber: string
  /** yyyy-mm-dd. */
  startDatum: string
  /** yyyy-mm-dd. */
  eindDatum: string
  omschrijving: string | null
  /** Aantal dag-entries in de gevraagde week (waarom deze bon in de rest valt). */
  dagenInWeek: number
}

/** De roosterkeuzes voor de fix-actie: alleen roosters van dezelfde klant. */
export interface DekkingRoosterKeuzeDTO {
  id: string
  naam: string
}

export interface DekkingNietToegekendResponse {
  werkbonnen: OngekoppeldeWerkbonDTO[]
  roosters: DekkingRoosterKeuzeDTO[]
}

/**
 * FO-348 §7 — koppel een werkbon alsnog aan een rooster. De API valideert dat
 * het rooster bij dezelfde klant hoort als de bon; een koppeling naar een ander
 * klantrooster zou de dekking van die klant vervuilen.
 */
export const koppelWerkbonRoosterSchema = z.object({
  werkbonId: z.string().min(1),
  workScheduleId: z.string().min(1),
})
export type KoppelWerkbonRoosterInput = z.infer<typeof koppelWerkbonRoosterSchema>

// ─── batch-49 in-te-plannen (FO-398 §3.2 + §6) ──────────────────────────────
//
// ÉÉN gedeelde server-actie "ontkoppel toekomstige planning". Alle instroom-
// triggers (uitdienst-dialoog, ziekmelding-signaal, verlof-goedkeuring,
// opschoonrun) lopen hierdoorheen zodat de guards uit §6.1 maar op één plek
// staan: geboekte uren en vergrendelde weken zijn NOOIT ontkoppelbaar.

/** Herkomst die de aanroeper op de ontkoppelde regels zet (spec §3.2). */
export const ONTKOPPEL_HERKOMSTEN = [
  'UITDIENST',
  'UITDIENST_OPSCHONING',
  'LOSGEKOPPELD',
  'ZIEKTE',
  'VERLOF',
] as const
export type OntkoppelHerkomst = (typeof ONTKOPPEL_HERKOMSTEN)[number]

/** Per werkbon kiest de gebruiker expliciet (§6.2 — geen stil default-doorklikken). */
export const ONTKOPPEL_KEUZES = ['IN_TE_PLANNEN', 'ANNULEREN', 'LATEN_STAAN'] as const
export type OntkoppelKeuze = (typeof ONTKOPPEL_KEUZES)[number]

export const ontkoppelWerkbonKeuzeSchema = z
  .object({
    werkbonId: z.string().min(1),
    keuze: z.enum(ONTKOPPEL_KEUZES),
    /** §6.4 — annuleren vereist een reden (audit). */
    reden: z.string().trim().max(500).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.keuze === 'ANNULEREN' && (val.reden ?? '').trim().length < 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reden'],
        message: 'Geef een reden op (minimaal 3 tekens) bij annuleren.',
      })
    }
  })
export type OntkoppelWerkbonKeuze = z.infer<typeof ontkoppelWerkbonKeuzeSchema>

export const ontkoppelToekomstigePlanningSchema = z.object({
  employeeId: z.string().min(1),
  /**
   * Alleen dagregels NÁ deze dag worden geraakt (yyyy-mm-dd, exclusief). Bij
   * uitdienst is dit de einddatum: t/m die datum werkt de medewerker gewoon
   * door, dus die regels blijven staan (§3.2).
   */
  vanafNa: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'vanafNa moet yyyy-mm-dd zijn'),
  /**
   * Optionele BOVENgrens (yyyy-mm-dd, inclusief). Bij ziekte/verlof is de
   * afwezigheid begrensd: zonder deze grens zou "zet in te plannen" bij een
   * tweedaagse griep ALLE toekomstige planning van de monteur meenemen (A2 zegt
   * juist dat de teamleider de duur bepaalt). Bij uitdienst blijft hij leeg -
   * daar is alles ná de einddatum aan de orde.
   */
  totEnMet: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'totEnMet moet yyyy-mm-dd zijn')
    .optional(),
  herkomst: z.enum(ONTKOPPEL_HERKOMSTEN),
  keuzes: z.array(ontkoppelWerkbonKeuzeSchema).min(1).max(200),
})
export type OntkoppelToekomstigePlanningInput = z.infer<typeof ontkoppelToekomstigePlanningSchema>

/** Eén werkbon in het voorstel dat de uitdienst-/actie-dialoog toont. */
export interface ToekomstigePlanningWerkbonDTO {
  werkbonId: string
  werkbonNumber: string
  client: { id: string; name: string } | null
  omschrijving: string | null
  aantalDagen: number
  eersteDatum: string
  laatsteDatum: string
  /**
   * §6.1 — false zodra één dag geboekte uren heeft of in een vergrendelde week
   * valt. De dialoog toont dan de uitleg i.p.v. een keuze; de server weigert
   * zo'n regel hoe dan ook (409).
   */
  ontkoppelbaar: boolean
  /** Waarom niet ontkoppelbaar (leeg als het wel kan). */
  blokkadeReden: string | null
}

export interface ToekomstigePlanningVoorstelDTO {
  employeeId: string
  employeeNaam: string
  /** yyyy-mm-dd — regels t/m deze dag blijven staan. */
  vanafNa: string
  /** yyyy-mm-dd (inclusief) — bovengrens bij een begrensde afwezigheid. */
  totEnMet: string | null
  werkbonnen: ToekomstigePlanningWerkbonDTO[]
  totaalDagen: number
}

export interface OntkoppelToekomstigePlanningResultaat {
  inTePlannen: number
  geannuleerd: number
  overgeslagen: number
  /**
   * Per werkbon hoeveel dagregels er daadwerkelijk verhuisd/vervallen zijn.
   * Nodig omdat de instroom-melding PER WERKBON vuurt: zonder deze uitsplitsing
   * zou elke melding het TOTAAL noemen ("6 diensten zonder uitvoerder" bij drie
   * bonnen van elk twee dagen). Alleen bonnen die echt geraakt zijn staan erin.
   */
  perWerkbon: Array<{ werkbonId: string; inTePlannen: number; geannuleerd: number }>
}

// ─── Spec 110 §3.4-uitbreiding — verwijderen met reeks-scope ────────────────
//
// Een terugkerende dienst leeft als reeks: een moeder-werkbon met week-kinderen
// (`parentWerkbonId`). "Alleen deze week" verwijderen laat de rest van de reeks
// elke volgende week opnieuw in het vak opduiken; de reeks-scope ruimt alle
// onbemande dagen van de hele familie in één keer op. De reeks-info voedt de
// keuze-dialoog: de omvang vooraf, plus de "al ingevuld?"-hint (bemande
// planning bij dezelfde klant in dezelfde periode, op een andere werkbon).

export const VERWIJDER_IN_TE_PLANNEN_SCOPES = ['venster', 'reeks'] as const
export type VerwijderInTePlannenScope = (typeof VERWIJDER_IN_TE_PLANNEN_SCOPES)[number]

/**
 * Gedeeld request-schema (CLAUDE.md, Validatie & foutmeldingen): de API parset
 * hiermee en de backoffice-action stuurt dezelfde vorm. Zonder `scope` blijft
 * het de bestaande venster-verwijdering (backward compatible).
 */
export const verwijderInTePlannenRequestSchema = z
  .object({
    werkbonId: z.string().min(1),
    scope: z.enum(VERWIJDER_IN_TE_PLANNEN_SCOPES).default('venster'),
    /** Bordvenster (yyyy-mm-dd, inclusief) — alleen relevant bij scope venster. */
    vanaf: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'vanaf moet yyyy-mm-dd zijn')
      .optional(),
    tot: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'tot moet yyyy-mm-dd zijn')
      .optional(),
  })
  .superRefine((val, ctx) => {
    if (val.scope !== 'venster') return
    for (const veld of ['vanaf', 'tot'] as const) {
      if (!val[veld]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [veld],
          message: `${veld} is verplicht bij scope venster`,
        })
      }
    }
  })
export type VerwijderInTePlannenRequest = z.infer<typeof verwijderInTePlannenRequestSchema>

/** Eén "al ingevuld?"-regel: bemande planning bij dezelfde klant in de periode. */
export interface InTePlannenDekkingDTO {
  werkbonNumber: string
  monteurNaam: string
  /** Eerste/laatste bemande dag van deze monteur op die werkbon (ISO). */
  van: string
  tot: string
  dagen: number
}

/** Omvang + context van de reeks-familie, voor de verwijder-dialoog. */
export interface InTePlannenReeksInfoDTO {
  /** Werkbonnen in de familie (moeder + kinderen) mét onbemande dagen. */
  aantalWerkbonnen: number
  /** Alle onbemande dagen van de familie, ongeacht het bordvenster. */
  totaalDagen: number
  eersteDatum: string | null
  laatsteDatum: string | null
  /** Gecapt op een handvol regels; `dekkingTotaal` houdt de teller eerlijk. */
  dekking: InTePlannenDekkingDTO[]
  dekkingTotaal: number
}

export interface VerwijderInTePlannenGeblokkeerdDTO {
  werkbonNumber: string
  reden: string
  dagen: number
}

/**
 * Resultaat van scope `reeks`: overslaan-en-benoemen per geblokkeerde werkbon
 * i.p.v. het alles-of-niets van het venster-pad — één vergrendelde zuster-week
 * mag de opruiming van de rest niet tegenhouden.
 */
export interface VerwijderInTePlannenReeksResultaatDTO {
  verwijderd: number
  /** Aantal werkbonnen waarvan daadwerkelijk dagen zijn verwijderd. */
  werkbonnen: number
  geblokkeerd: VerwijderInTePlannenGeblokkeerdDTO[]
  /**
   * Dagen die de race verloren (net toegewezen/geboekt tussen lezen en
   * schrijven) — apart benoemd zodat "verwijderd" nooit stil minder is dan
   * wat de planner in de dialoog zag.
   */
  overgeslagenDoorRace: number
  /** Nummer van de aangeklikte werkbon, voor de toast. */
  werkbonNumber: string
}
